import React, { useEffect, useState, useMemo, useRef } from "react";
import api, { STAGE_LABELS, WORK_STAGES, STAGE_GIVEN_OPTIONS, STAGE_GIVEN_DEFAULT, formatHrLabel } from "@/lib/api";
import useDraft from "@/lib/useDraft";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Upload, Download, FileSpreadsheet, AlertTriangle, Flame, Copy, History, Globe, LayoutGrid, Mail } from "lucide-react";
import { toast } from "sonner";
import KanbanColumn from "@/components/jobs/KanbanColumn";
import StageAuditModal from "@/components/jobs/StageAuditModal";
import SurveydraftsJobPanel from "@/components/jobs/SurveydraftsJobPanel";
import OutlookJobsView from "@/components/jobs/OutlookJobsView";

const BOARD_STAGES = ["assigned", "in_field", "drafter", "qa1", "qa2", "final_qc", "sent_back_to_field", "for_revision", "sent_to_client"];
const STAGE_BG = {
  assigned: "bg-zinc-200",
  in_field: "bg-amber-100",
  drafter: "bg-blue-100",
  qa1: "bg-yellow-100",
  qa2: "bg-orange-100",
  final_qc: "bg-indigo-100",
  sent_back_to_field: "bg-rose-100",
  for_revision: "bg-pink-100",
  sent_to_client: "bg-green-100",
};
const PRIORITY_STYLES = {
  urgent: { bg: "bg-red-200", icon: Flame, label: "URGENT" },
  high: { bg: "bg-orange-200", icon: AlertTriangle, label: "HIGH" },
  normal: { bg: "bg-zinc-100", icon: null, label: "NORMAL" },
  low: { bg: "bg-zinc-50", icon: null, label: "LOW" },
};

export default function Jobs() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || (user?.team_role || "").toLowerCase() === "admin";
  const [board, setBoard] = useState({});
  const [users, setUsers] = useState([]);
  const [teamGroups, setTeamGroups] = useState([]);   // [{ team, members: [...] }]
  const [clients, setClients] = useState([]);
  const [assigneeStats, setAssigneeStats] = useState({ assignees: [], month: "" });
  const [filterAssignee, setFilterAssignee] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterTeam, setFilterTeam] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterCategory, setFilterCategory] = useState("");  // "", live, practice, training
  const [filterStale, setFilterStale] = useState(false);  // toggle: only show stuck jobs
  const [filterVendorId, setFilterVendorId] = useState("");   // surveydrafts_vendor_id
  const [filterVendorName, setFilterVendorName] = useState("");
  const [stale, setStale] = useState({ count: 0, jobs: [] });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [showAudit, setShowAudit] = useState(false);   // global stage-transition history
  const [auditForJob, setAuditForJob] = useState(null); // per-job history (from detail modal)
  const [selected, setSelected] = useState(null);
  const [draggedJobId, setDraggedJobId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("jobs_view_mode") || "outlook"; } catch { return "outlook"; }
  });
  useEffect(() => { try { localStorage.setItem("jobs_view_mode", viewMode); } catch { /* ignore */ } }, [viewMode]);
  const fileRef = React.useRef(null);

  const load = async () => {
    const [b, u, s, c, st, t] = await Promise.all([
      api.get("/jobs/board"),
      api.get("/users"),
      api.get("/jobs/assignee-stats"),
      api.get("/clients/visible").catch(() => ({ data: [] })),
      api.get("/jobs/stale").catch(() => ({ data: { count: 0, jobs: [] } })),
      api.get("/team/teams").catch(() => ({ data: { teams: [] } })),
    ]);
    setBoard(b.data);
    setUsers(u.data);
    setAssigneeStats(s.data);
    setClients(c.data || []);
    setStale(st.data || { count: 0, jobs: [] });
    setTeamGroups((t.data?.teams) || []);
  };

  // Map jobId -> { days_in_stage, threshold_days, priority } so JobCard can render
  // the STUCK badge without each card re-fetching.
  const staleMap = useMemo(() => {
    const m = {};
    for (const r of stale.jobs || []) m[r.id] = r;
    return m;
  }, [stale]);

  useEffect(() => {
    if (location.search.includes("filter=stale")) setFilterStale(true);
    const params = new URLSearchParams(location.search);
    const vid = params.get("vendor_id");
    const vname = params.get("vendor_name");
    if (vid) setFilterVendorId(vid);
    if (vname) setFilterVendorName(vname);
  }, []);
  useEffect(() => { load(); }, []);

  // Map full client name → initials (used to hide confidential names from non-admins)
  const clientLabelByName = useMemo(() => {
    const m = {};
    for (const c of clients || []) {
      if (c.name) m[c.name] = c.initials || c.name;
    }
    return m;
  }, [clients]);
  const labelForJobClient = (j) => {
    if (!j) return "";
    if (isAdmin) return j.client || "";
    const byId = (clients || []).find((c) => c.id === j.client_id);
    if (byId) return byId.initials || "";
    if (j.client && clientLabelByName[j.client]) return clientLabelByName[j.client];
    return "";
  };

  const advance = async (job, target) => {
    try {
      await api.patch(`/jobs/${job.id}`, { stage: target, status: target === "sent_to_client" ? "sent_to_client" : "in_progress" });
      toast.success(`Moved to ${STAGE_LABELS[target]}`);
      load();
    } catch (e) { toast.error("Failed to move job"); }
  };

  // -------- Drag-and-drop wiring (HTML5 native — desktop only; mobile uses the Move-to dropdown) --------
  const findJob = (jobId) => {
    for (const arr of Object.values(board)) {
      const j = (arr || []).find((x) => x.id === jobId);
      if (j) return j;
    }
    return null;
  };
  const onDragStart = (e, jobId) => {
    setDraggedJobId(jobId);
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", jobId);
    } catch { /* Safari sometimes throws on setData of empty payloads */ }
  };
  const onDragEnd = () => { setDraggedJobId(null); setDragOverStage(null); };
  const onColumnDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverStage !== stage) setDragOverStage(stage);
  };
  const onColumnDrop = async (e, stage) => {
    e.preventDefault();
    const jobId = draggedJobId || e.dataTransfer.getData("text/plain");
    setDraggedJobId(null); setDragOverStage(null);
    if (!jobId) return;
    const job = findJob(jobId);
    if (!job || job.stage === stage) return;
    await advance(job, stage);
  };

  // Filter board by assignee + client + team + priority
  const filteredBoard = React.useMemo(() => {
    const out = {};
    Object.entries(board).forEach(([stage, jobs]) => {
      out[stage] = (jobs || []).filter((j) => {
        if (filterAssignee) {
          const sa = j.stage_assignees || {};
          if (!Object.values(sa).some((v) => v === filterAssignee)) return false;
        }
        if (filterClient && j.client_id !== filterClient && j.client !== filterClient) return false;
        if (filterTeam && j.team !== filterTeam) return false;
        if (filterPriority && (j.priority || "normal") !== filterPriority) return false;
        if (filterCategory && (j.category || "live") !== filterCategory) return false;
        if (filterStale && !staleMap[j.id]) return false;
        if (filterVendorId && j.surveydrafts_vendor_id !== filterVendorId) return false;
        return true;
      });
    });
    return out;
  }, [board, filterAssignee, filterClient, filterTeam, filterPriority, filterCategory, filterStale, filterVendorId, staleMap]);

  const teams = React.useMemo(() => {
    const set = new Set();
    Object.values(board).flat().forEach((j) => { if (j.team) set.add(j.team); });
    clients.forEach((c) => { if (c.team) set.add(c.team); });
    return [...set].sort();
  }, [board, clients]);

  // Bidirectional client↔team filtering: if team selected, only show clients with that team; if client selected, lock team
  const clientsForDropdown = React.useMemo(() => {
    if (!filterTeam) return clients;
    return clients.filter((c) => c.team === filterTeam);
  }, [clients, filterTeam]);

  const teamsForDropdown = React.useMemo(() => {
    if (!filterClient) return teams;
    const c = clients.find((x) => x.id === filterClient);
    return c?.team ? [c.team] : teams;
  }, [teams, filterClient, clients]);

  const toggleSelect = (jobId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
      return next;
    });
  };

  const bulkAdvance = async (targetStage) => {
    if (selectedIds.size === 0) return;
    try {
      const { data } = await api.post("/jobs/bulk-advance", { job_ids: [...selectedIds], target_stage: targetStage });
      toast.success(`Advanced ${data.updated} jobs → ${STAGE_LABELS[targetStage]}`);
      setSelectedIds(new Set());
      load();
    } catch { toast.error("Bulk advance failed"); }
  };

  const duplicateJob = async (job) => {
    try {
      const { data } = await api.post(`/jobs/${job.id}/duplicate`);
      toast.success(`Duplicated as ${data.job_number}`);
      load();
    } catch { toast.error("Duplicate failed"); }
  };

  const isOverdue = (j) => {
    if (!j.due_date) return false;
    if (j.status === "completed" || j.status === "cancelled" || j.status === "sent_to_client") return false;
    return new Date(j.due_date) < new Date(new Date().toDateString());
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/jobs/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    e.target.value = "";
  };

  const sdFileRef = useRef(null);
  const [sdConnected, setSdConnected] = useState(false);
  const [sdSyncing, setSdSyncing] = useState(false);
  const [sdInfo, setSdInfo] = useState(null);    // { auto_sync_minutes, last_sync_at, ... }

  const refreshSdStatus = () => {
    api.get("/integrations/surveydrafts/status")
      .then(({ data }) => { setSdConnected(!!data.connected); setSdInfo(data.info || null); })
      .catch(() => {});
  };
  useEffect(() => { refreshSdStatus(); }, []);

  const onImportSurveydrafts = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/jobs/import-surveydrafts", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(
        `Surveydrafts CSV: ${data.created} new, ${data.updated} updated, ${data.skipped} skipped (${data.clients_seen} clients)`,
        { duration: 8000 }
      );
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Surveydrafts import failed"); }
    e.target.value = "";
  };

  const onSyncSurveydrafts = async () => {
    setSdSyncing(true);
    try {
      const { data } = await api.post("/jobs/sync-surveydrafts");
      toast.success(
        `Surveydrafts sync: ${data.created} new, ${data.updated} updated, ${data.skipped} skipped`,
        { duration: 8000 }
      );
      load();
      refreshSdStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
    setSdSyncing(false);
  };

  const onChangeSdSchedule = async () => {
    const current = sdInfo?.auto_sync_minutes ?? 30;
    const input = prompt(
      `Auto-sync interval (in minutes). Enter 0 to disable, or 5–1440 to enable.\nCurrent: ${current}m`,
      String(current),
    );
    if (input === null) return;
    const mins = parseInt(input, 10);
    if (Number.isNaN(mins)) { toast.error("Enter a number"); return; }
    try {
      await api.patch("/integrations/surveydrafts/schedule", { auto_sync_minutes: mins });
      toast.success(mins === 0 ? "Auto-sync disabled" : `Auto-sync set to every ${mins}m`);
      refreshSdStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to set schedule"); }
  };

  const onDisconnectSurveydrafts = async () => {
    if (!confirm("Disconnect surveydrafts.us? You'll need to re-enter credentials to reconnect.")) return;
    try {
      await api.delete("/integrations/surveydrafts");
      toast.success("Surveydrafts disconnected");
      setSdConnected(false);
      setSdInfo(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to disconnect"); }
  };

  const onConnectSurveydrafts = async () => {
    const email = prompt("Surveydrafts.us login email");
    if (!email) return;
    const password = prompt("Surveydrafts.us password");
    if (!password) return;
    try {
      await api.post("/integrations/surveydrafts/connect", { email, password });
      toast.success("Surveydrafts connected. Click SYNC NOW to pull jobs.");
      setSdConnected(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not connect"); }
  };

  const onTogglePush = async () => {
    const next = !(sdInfo?.push_enabled !== false);   // default true, so click flips to false
    try {
      await api.patch("/integrations/surveydrafts/push", { push_enabled: next });
      toast.success(next ? "2-way push ENABLED — stage changes will flow back to surveydrafts.us" : "2-way push DISABLED");
      refreshSdStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to toggle push"); }
  };

  const onToggleExtendedAutoSync = async () => {
    const next = !(sdInfo?.extended_auto_sync !== false);
    try {
      await api.patch("/integrations/surveydrafts/extended-auto-sync", { enabled: next });
      toast.success(next
        ? "Extended auto-sync ENABLED — vendors/teams/daily-jobs will refresh on every scheduler tick"
        : "Extended auto-sync DISABLED — manual SYNC EXT only");
      refreshSdStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to toggle"); }
  };

  const onSetSdApiKey = async () => {
    const choice = prompt(
      "Surveydrafts X-API-Key — leave BLANK to auto-generate via your stored JWT, OR paste an existing key:",
      "",
    );
    if (choice === null) return;
    try {
      const body = choice.trim() ? { api_key: choice.trim() } : {};
      const { data } = await api.post("/integrations/surveydrafts/api-key", body);
      toast.success(`API key set (${data.preview})`);
      refreshSdStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to set API key"); }
  };

  const onSyncSurveydraftsExtended = async () => {
    setSdSyncing(true);
    try {
      const { data } = await api.post("/jobs/sync-surveydrafts-extended");
      if (data.ok === false) {
        toast.error(data.reason || "Extended sync failed");
      } else {
        const c = data.counts || {};
        toast.success(
          `Extended sync: vendors +${c.vendors?.created||0}/${c.vendors?.updated||0}, ` +
          `team +${c.team_members?.created||0}/${c.team_members?.updated||0}, ` +
          `daily-jobs +${c.daily_jobs?.created||0}/${c.daily_jobs?.updated||0}`,
          { duration: 9000 },
        );
        load();
        refreshSdStatus();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Extended sync failed"); }
    setSdSyncing(false);
  };

  const downloadFile = async (url, name) => {
    const token = localStorage.getItem("session_token");
    const res = await api.get(url, { params: token ? { auth: token } : {}, responseType: "blob" });
    const u = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = u; a.download = name; a.click();
    URL.revokeObjectURL(u);
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">PIPELINE</div>
          <h1 className="font-heading text-4xl font-black tracking-tight leading-none mt-1">Jobs Board</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="flex items-center border border-zinc-900 bg-white overflow-hidden" data-testid="jobs-view-toggle">
            <button
              onClick={() => setViewMode("outlook")}
              className={`px-2.5 py-2 text-[10px] font-mono font-bold flex items-center gap-1 ${viewMode === "outlook" ? "bg-yellow-200" : "hover:bg-zinc-100"}`}
              data-testid="view-mode-outlook"
              title="Outlook-style: categories → list → details"
            >
              <Mail className="w-3.5 h-3.5" /> OUTLOOK
            </button>
            <button
              onClick={() => setViewMode("kanban")}
              className={`px-2.5 py-2 text-[10px] font-mono font-bold flex items-center gap-1 border-l border-zinc-900 ${viewMode === "kanban" ? "bg-yellow-200" : "hover:bg-zinc-100"}`}
              data-testid="view-mode-kanban"
              title="Classic Kanban board with drag-and-drop"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> KANBAN
            </button>
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={onImport} className="hidden" data-testid="jobs-import-input" />
          <input ref={sdFileRef} type="file" accept=".csv,.xlsx" onChange={onImportSurveydrafts} className="hidden" data-testid="jobs-import-sd-input" />
          <button onClick={() => fileRef.current?.click()} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="jobs-import-btn">
            <Upload className="w-3.5 h-3.5" /> IMPORT
          </button>
          <button
            onClick={() => sdFileRef.current?.click()}
            className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1"
            data-testid="jobs-import-sd-btn"
            title="Upload a surveydrafts.us CSV/XLSX export manually"
          >
            <Upload className="w-3.5 h-3.5" /> SD CSV
          </button>
          {sdConnected ? (
            <>
              <button
                onClick={onSyncSurveydrafts}
                disabled={sdSyncing}
                className="brut-btn px-3 py-2 text-xs flex items-center gap-1 bg-emerald-300 hover:bg-emerald-400 disabled:opacity-60"
                data-testid="jobs-sync-sd-btn"
                title="Pull latest jobs from surveydrafts.us right now"
              >
                <Globe className="w-3.5 h-3.5" /> {sdSyncing ? "SYNCING…" : "SYNC SURVEYDRAFTS"}
              </button>
              <button
                onClick={onChangeSdSchedule}
                className="brut-btn-secondary px-2 py-2 text-[10px] font-mono"
                data-testid="jobs-sd-schedule-btn"
                title={sdInfo?.last_sync_at ? `Last auto-sync: ${sdInfo.last_sync_at.slice(0,16).replace('T',' ')}` : "Click to change auto-sync cadence"}
              >
                {(sdInfo?.auto_sync_minutes ?? 0) > 0 ? `AUTO ${sdInfo.auto_sync_minutes}M` : "MANUAL"}
              </button>
              <button
                onClick={onTogglePush}
                className={`brut-btn-secondary px-2 py-2 text-[10px] font-mono ${sdInfo?.push_enabled === false ? "text-stone-500" : "text-emerald-700"}`}
                data-testid="jobs-sd-push-btn"
                title={sdInfo?.push_enabled === false ? "2-way push DISABLED — click to enable" : "2-way push ENABLED — stage changes auto-flow back to surveydrafts.us"}
              >
                PUSH {sdInfo?.push_enabled === false ? "OFF" : "ON"}
              </button>
              <button
                onClick={onSetSdApiKey}
                className={`brut-btn-secondary px-2 py-2 text-[10px] font-mono ${sdInfo?.api_key_set_at ? "text-emerald-700" : "text-amber-700"}`}
                data-testid="jobs-sd-api-key-btn"
                title={sdInfo?.api_key_set_at ? `API key set ${sdInfo.api_key_set_at.slice(0,10)} — click to rotate` : "Set X-API-Key for vendors / teams / daily-jobs sync"}
              >
                {sdInfo?.api_key_set_at ? "KEY ✓" : "KEY ?"}
              </button>
              <button
                onClick={onSyncSurveydraftsExtended}
                disabled={sdSyncing || !sdInfo?.api_key_set_at}
                className="brut-btn-secondary px-2 py-2 text-[10px] font-mono disabled:opacity-40"
                data-testid="jobs-sd-sync-ext-btn"
                title={sdInfo?.api_key_set_at ? "Sync vendors / teams / daily-jobs" : "Set API key first"}
              >
                SYNC EXT
              </button>
              {sdInfo?.api_key_set_at && (
                <button
                  onClick={onToggleExtendedAutoSync}
                  className={`brut-btn-secondary px-2 py-2 text-[10px] font-mono ${sdInfo?.extended_auto_sync === false ? "text-stone-500" : "text-emerald-700"}`}
                  data-testid="jobs-sd-ext-auto-btn"
                  title={sdInfo?.extended_auto_sync === false
                    ? "Extended auto-sync DISABLED — click to enable (will chain after every basic sync tick)"
                    : "Extended auto-sync ENABLED — runs after every scheduler tick"}
                >
                  EXT AUTO {sdInfo?.extended_auto_sync === false ? "OFF" : "ON"}
                </button>
              )}
              <button
                onClick={onDisconnectSurveydrafts}
                className="brut-btn-secondary px-2 py-2 text-[10px] font-mono text-red-700"
                data-testid="jobs-sd-disconnect-btn"
                title="Disconnect surveydrafts (forget saved credentials)"
              >
                <X className="w-3 h-3" />
              </button>
            </>
          ) : (
            <button
              onClick={onConnectSurveydrafts}
              className="brut-btn px-3 py-2 text-xs flex items-center gap-1 bg-emerald-300 hover:bg-emerald-400"
              data-testid="jobs-connect-sd-btn"
              title="Save your surveydrafts.us credentials (encrypted) to enable one-click sync"
            >
              <Globe className="w-3.5 h-3.5" /> CONNECT SURVEYDRAFTS
            </button>
          )}
          <button onClick={() => downloadFile("/jobs/template", "jobs_template.xlsx")} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="jobs-template-btn">
            <FileSpreadsheet className="w-3.5 h-3.5" /> TEMPLATE
          </button>
          <button onClick={() => downloadFile("/jobs/export?format=xlsx", "jobs.xlsx")} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="jobs-export-btn">
            <Download className="w-3.5 h-3.5" /> EXPORT
          </button>
          <button onClick={() => setShowAudit(true)} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="jobs-audit-btn">
            <History className="w-3.5 h-3.5" /> AUDIT LOG
          </button>
          <button onClick={() => setShowCreate(true)} className="brut-btn px-4 py-2 text-sm flex items-center gap-2" data-testid="create-job-btn">
            <Plus className="w-4 h-4" /> NEW JOB
          </button>
        </div>
      </div>

      {/* Filters row */}
      <div className="brut-card p-3 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[150px]">
          <div className="label-mono mb-1">CLIENT</div>
          <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)} className="brut-input w-full px-2 py-1.5 text-sm" data-testid="filter-client">
            <option value="">All clients</option>
            {clientsForDropdown.map((c) => <option key={c.id} value={c.id}>{isAdmin ? c.name : (c.initials || c.name)}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <div className="label-mono mb-1">TEAM</div>
          <select value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)} className="brut-input w-full px-2 py-1.5 text-sm font-mono">
            <option value="">All teams</option>
            {teamsForDropdown.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <div className="label-mono mb-1">PRIORITY</div>
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="brut-input w-full px-2 py-1.5 text-sm font-mono">
            <option value="">All</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <div className="label-mono mb-1">CATEGORY</div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="brut-input w-full px-2 py-1.5 text-sm font-mono"
            data-testid="filter-category"
          >
            <option value="">All</option>
            <option value="live">Live (billable)</option>
            <option value="practice">Practice</option>
            <option value="training">Training</option>
          </select>
        </div>
        {(filterClient || filterTeam || filterPriority || filterCategory || filterAssignee || filterStale || filterVendorId) && (
          <button onClick={() => { setFilterClient(""); setFilterTeam(""); setFilterPriority(""); setFilterCategory(""); setFilterAssignee(""); setFilterStale(false); setFilterVendorId(""); setFilterVendorName(""); }} className="brut-btn-secondary px-3 py-1.5 text-xs mt-5" data-testid="clear-filters">CLEAR</button>
        )}
        {filterVendorId && (
          <div
            className="px-3 py-1.5 text-xs mt-5 border border-zinc-900 font-mono font-bold flex items-center gap-2 bg-emerald-200"
            data-testid="filter-vendor-pill"
          >
            <Globe className="w-3 h-3" />
            TEAM: {filterVendorName || filterVendorId.slice(0, 8)}
            <button
              onClick={() => { setFilterVendorId(""); setFilterVendorName(""); }}
              className="text-zinc-700 hover:text-red-700"
              title="Clear team filter"
              data-testid="clear-vendor-filter"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        {stale.count > 0 && (
          <button
            onClick={() => setFilterStale((v) => !v)}
            className={`px-3 py-1.5 text-xs mt-5 border border-zinc-900 font-mono font-bold flex items-center gap-1 ${
              filterStale ? "bg-orange-300" : "bg-orange-100 hover:bg-orange-200"
            }`}
            data-testid="filter-stale-pill"
            title="Show only jobs stuck longer than their priority threshold"
          >
            ⏳ STUCK · {stale.count}
          </button>
        )}
      </div>

      {/* Bulk action toolbar */}
      {selectedIds.size > 0 && (
        <div className="brut-card p-3 bg-yellow-100 flex items-center gap-2 flex-wrap" data-testid="bulk-toolbar">
          <span className="label-mono">{selectedIds.size} SELECTED</span>
          <span className="text-xs font-mono text-zinc-600 mx-2">→ ADVANCE TO:</span>
          {BOARD_STAGES.slice(1).map((s) => (
            <button key={s} onClick={() => bulkAdvance(s)} className="brut-btn-secondary px-2 py-1 text-xs" data-testid={`bulk-advance-${s}`}>
              {STAGE_LABELS[s].toUpperCase()}
            </button>
          ))}
          <button onClick={() => setSelectedIds(new Set())} className="ml-auto text-xs font-mono text-zinc-600 hover:underline">CLEAR</button>
        </div>
      )}

      {/* Assignee Stats — current month */}
      {assigneeStats.assignees?.length > 0 && (
        <AssigneeWorkloadStrip
          assigneeStats={assigneeStats}
          filterAssignee={filterAssignee}
          setFilterAssignee={setFilterAssignee}
          filterTeam={filterTeam}
          setFilterTeam={setFilterTeam}
          filterClient={filterClient}
          setFilterClient={setFilterClient}
          clients={clients}
          isAdmin={isAdmin}
        />
      )}

      {/* Capsule pill filters above kanban */}
      <KanbanPillFilters
        board={board}
        clients={clients}
        isAdmin={isAdmin}
        filterTeam={filterTeam}
        setFilterTeam={setFilterTeam}
        filterClient={filterClient}
        setFilterClient={setFilterClient}
      />

      {/* Kanban board — horizontal scroll on mobile/tablet, grid on desktop.
          items-start prevents columns from stretching to match the tallest one,
          so each column now only takes the space it needs. */}
      {viewMode === "kanban" ? (
        <div className="flex lg:grid lg:grid-cols-3 xl:grid-cols-6 gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory lg:overflow-visible lg:items-start">
          {BOARD_STAGES.map((s) => (
            <KanbanColumn
              key={s}
              stage={s}
              stages={BOARD_STAGES}
              jobs={filteredBoard[s]}
              bg={STAGE_BG[s]}
              priorityStyles={PRIORITY_STYLES}
              selectedIds={selectedIds}
              draggedJobId={draggedJobId}
              dragOverStage={dragOverStage}
              isOverdue={isOverdue}
              staleMap={staleMap}
              labelForJobClient={labelForJobClient}
              onDragOver={onColumnDragOver}
              onDragLeave={() => setDragOverStage(null)}
              onDrop={onColumnDrop}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onSelect={setSelected}
              onToggleSelect={toggleSelect}
              onAdvance={advance}
              onDuplicate={duplicateJob}
            />
          ))}
        </div>
      ) : (
        <OutlookJobsView
          board={board}
          filteredBoard={filteredBoard}
          users={users}
          clients={clients}
          teams={teamGroups}
          isAdmin={isAdmin}
          staleMap={staleMap}
          isOverdue={isOverdue}
          labelForJobClient={labelForJobClient}
          onChanged={load}
          onDuplicate={duplicateJob}
          onAudit={(j) => setAuditForJob(j)}
        />
      )}

      {showCreate && <JobCreateModal users={users} clients={clients} teams={teamGroups} onClose={() => setShowCreate(false)} onCreated={load} />}
      {selected && <JobDetailModal job={selected} users={users} clients={clients} teams={teamGroups} onClose={() => setSelected(null)} onChanged={load} onDuplicate={() => { duplicateJob(selected); setSelected(null); }} onAudit={(j) => setAuditForJob(j)} />}
      {showAudit && <StageAuditModal onClose={() => setShowAudit(false)} />}
      {auditForJob && <StageAuditModal job={auditForJob} onClose={() => setAuditForJob(null)} />}
    </div>
  );
}

// ===========================================================================
// AssigneeWorkloadStrip — top tile strip with All / By Team / By Client
// grouping pills, and Active | Past sub-tab per user tile.
function AssigneeWorkloadStrip({
  assigneeStats, filterAssignee, setFilterAssignee,
  filterTeam, setFilterTeam, filterClient, setFilterClient,
  clients, isAdmin,
}) {
  const [groupBy, setGroupBy] = React.useState("all");   // all | team | client
  const [metricView, setMetricView] = React.useState("active");  // active | past

  // Group the assignees according to groupBy
  const groups = React.useMemo(() => {
    if (groupBy === "all") return [{ key: "ALL", label: null, people: assigneeStats.assignees }];
    const bucket = {};
    for (const p of assigneeStats.assignees) {
      const keys = groupBy === "team" ? (p.teams?.length ? p.teams : ["—"])
                                       : (p.clients?.length ? p.clients : ["—"]);
      for (const k of keys) {
        if (!bucket[k]) bucket[k] = [];
        bucket[k].push(p);
      }
    }
    return Object.entries(bucket)
      .sort(([, aa], [, bb]) => bb.length - aa.length)
      .map(([key, people]) => ({ key, label: key, people }));
  }, [groupBy, assigneeStats.assignees]);

  const onTileClick = (p) => {
    setFilterAssignee(filterAssignee === p.name ? "" : p.name);
  };

  const clientLabel = (raw) => {
    if (isAdmin) return raw;
    const c = (clients || []).find((x) => x.name === raw);
    return c?.initials || raw;
  };

  return (
    <div className="brut-card p-4" data-testid="assignee-workload-strip">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="label-mono">ASSIGNEE WORKLOAD</div>
          <div className="font-heading text-xl font-bold leading-tight">
            Active jobs + {new Date().toLocaleDateString(undefined, { month: 'long' })} hours &amp; efficiency
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Group by pills */}
          <div className="flex items-center gap-1" data-testid="group-by-pills">
            <span className="label-mono text-zinc-500 mr-1">GROUP</span>
            {[
              { v: "all", label: "All Users" },
              { v: "team", label: "By Team" },
              { v: "client", label: "By Client" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setGroupBy(opt.v)}
                className={`px-2.5 py-1 text-[10px] font-mono font-bold rounded-full border border-zinc-900 transition-all ${
                  groupBy === opt.v ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-100"
                }`}
                data-testid={`group-by-${opt.v}`}
              >{opt.label}</button>
            ))}
          </div>
          {/* Active | Past sub-tab */}
          <div className="flex items-center border border-zinc-900 bg-white rounded-full overflow-hidden" data-testid="metric-view-tabs">
            {["active", "past"].map((v) => (
              <button
                key={v}
                onClick={() => setMetricView(v)}
                className={`px-2.5 py-1 text-[10px] font-mono font-bold ${
                  metricView === v ? "bg-yellow-200" : "hover:bg-zinc-100"
                }`}
                data-testid={`metric-tab-${v}`}
              >{v === "active" ? "ACTIVE" : "PAST"}</button>
            ))}
          </div>
          {filterAssignee && (
            <button onClick={() => setFilterAssignee("")} className="brut-btn-secondary px-3 py-1.5 text-xs flex items-center gap-1" data-testid="clear-assignee-filter">
              <X className="w-3 h-3" /> CLEAR: {filterAssignee}
            </button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {groups.map((g) => (
          <div key={g.key}>
            {g.label && (
              <div className="flex items-center gap-2 mb-1.5">
                <button
                  onClick={() => {
                    if (groupBy === "team") setFilterTeam(filterTeam === g.label ? "" : g.label);
                    else if (groupBy === "client") {
                      const c = (clients || []).find((x) => x.name === g.label);
                      setFilterClient(filterClient === (c?.id || g.label) ? "" : (c?.id || g.label));
                    }
                  }}
                  className={`px-3 py-1 text-[11px] font-mono font-bold rounded-full border border-zinc-900 ${
                    (groupBy === "team" && filterTeam === g.label) ||
                    (groupBy === "client" && (clients.find((c) => c.id === filterClient)?.name === g.label))
                      ? "bg-yellow-300" : "bg-white hover:bg-yellow-100"
                  }`}
                  data-testid={`group-header-${g.key}`}
                  title={`Click to filter kanban by ${groupBy === 'team' ? 'team' : 'client'}`}
                >
                  {groupBy === "client" ? clientLabel(g.label) : g.label}
                  <span className="ml-2 text-zinc-500">{g.people.length}</span>
                </button>
              </div>
            )}
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
              {g.people.map((p) => {
                const active = filterAssignee === p.name;
                const effColor = p.month_efficiency >= 100 ? "bg-green-200" : p.month_efficiency >= 70 ? "bg-yellow-200" : p.month_efficiency > 0 ? "bg-red-200" : "bg-zinc-100";
                const bigValue = metricView === "active" ? p.active_jobs : (p.past_jobs || 0);
                const bigLabel = metricView === "active" ? "ACTIVE" : "PAST";
                return (
                  <button
                    key={`${g.key}-${p.name}`}
                    onClick={() => onTileClick(p)}
                    data-testid={`assignee-card-${p.name}`}
                    className={`flex-shrink-0 w-48 border border-zinc-900 p-3 text-left transition-all ${active ? "bg-yellow-100 shadow-brut" : "bg-white hover:shadow-brut"}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 bg-yellow-200 border border-zinc-900 flex items-center justify-center font-mono text-xs font-bold flex-shrink-0">
                        {p.name?.[0]?.toUpperCase()}
                      </div>
                      <div className="font-bold text-sm truncate">{p.name}</div>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="label-mono">{bigLabel}</span>
                      <span className="font-heading font-black text-2xl leading-none" data-testid={`assignee-${p.name}-big-value`}>{bigValue}</span>
                    </div>
                    <div className="border-t border-zinc-200 mt-2 pt-2 space-y-1 text-xs font-mono">
                      <div className="flex justify-between">
                        <span className="text-zinc-500">THIS MONTH</span>
                        <span>{p.month_entries} tasks</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">TAKEN</span>
                        <span>{p.month_taken_hours}h / {p.month_given_hours}h</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-500">EFF</span>
                        <span className={`px-1.5 py-0.5 border border-zinc-900 font-bold ${effColor}`}>{p.month_efficiency}%</span>
                      </div>
                    </div>
                    {p.roles?.length > 0 && (
                      <div className="flex flex-wrap gap-0.5 mt-2">
                        {p.roles.map((r) => (
                          <span key={r} className="text-[8px] font-mono font-bold px-1 py-0.5 bg-zinc-100 border border-zinc-900">{(STAGE_LABELS[r] || r).toUpperCase()}</span>
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===========================================================================
// KanbanPillFilters — capsule pills above the kanban board.
// Toggle [BY TEAM | BY CLIENT] dimension; pills then come from that dimension.
function KanbanPillFilters({ board, clients, isAdmin, filterTeam, setFilterTeam, filterClient, setFilterClient }) {
  const [dim, setDim] = React.useState("team");

  // Build counts of jobs per team/client across the full (unfiltered) board
  const counts = React.useMemo(() => {
    const teamMap = {};
    const clientMap = {};
    for (const arr of Object.values(board || {})) {
      for (const j of arr || []) {
        if (j.team) teamMap[j.team] = (teamMap[j.team] || 0) + 1;
        const ck = j.client_id || j.client;
        if (ck) clientMap[ck] = (clientMap[ck] || 0) + 1;
      }
    }
    return { teamMap, clientMap };
  }, [board]);

  const teamPills = React.useMemo(
    () => Object.entries(counts.teamMap).sort((a, b) => b[1] - a[1]),
    [counts.teamMap]
  );
  const clientPills = React.useMemo(() => {
    const rows = Object.entries(counts.clientMap).map(([key, n]) => {
      const c = (clients || []).find((x) => x.id === key) || (clients || []).find((x) => x.name === key);
      return { key, label: isAdmin ? (c?.name || key) : (c?.initials || c?.name || key), n, id: c?.id || key };
    });
    rows.sort((a, b) => b.n - a.n);
    return rows;
  }, [counts.clientMap, clients, isAdmin]);

  const pills = dim === "team" ? teamPills.map(([k, n]) => ({ key: k, label: k, n, id: k }))
                                : clientPills;

  const isActive = (p) => dim === "team" ? filterTeam === p.id : filterClient === p.id;
  const onClick = (p) => {
    if (dim === "team") setFilterTeam(isActive(p) ? "" : p.id);
    else setFilterClient(isActive(p) ? "" : p.id);
  };

  if (pills.length === 0) return null;

  return (
    <div className="brut-card p-3 flex flex-wrap items-center gap-2" data-testid="kanban-pill-filters">
      <span className="label-mono mr-1">SORT JOBS</span>
      <div className="flex items-center border border-zinc-900 bg-white rounded-full overflow-hidden" data-testid="kanban-dim-toggle">
        {[
          { v: "team", label: "BY TEAM" },
          { v: "client", label: "BY CLIENT" },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setDim(opt.v)}
            className={`px-3 py-1 text-[10px] font-mono font-bold ${dim === opt.v ? "bg-yellow-200" : "hover:bg-zinc-100"}`}
            data-testid={`kanban-dim-${opt.v}`}
          >{opt.label}</button>
        ))}
      </div>
      <div className="w-px h-5 bg-zinc-300 mx-1" />
      <button
        onClick={() => { setFilterTeam(""); setFilterClient(""); }}
        className={`px-2.5 py-1 text-[10px] font-mono font-bold rounded-full border border-zinc-900 ${
          !filterTeam && !filterClient ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-100"
        }`}
        data-testid="kanban-pill-all"
      >ALL</button>
      <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
        {pills.map((p) => (
          <button
            key={p.key}
            onClick={() => onClick(p)}
            className={`px-2.5 py-1 text-[10px] font-mono font-bold rounded-full border border-zinc-900 inline-flex items-center gap-1.5 ${
              isActive(p) ? "bg-yellow-300" : "bg-white hover:bg-yellow-100"
            }`}
            data-testid={`kanban-pill-${dim}-${p.key}`}
            title={`${p.n} job${p.n === 1 ? "" : "s"}`}
          >
            <span className="truncate max-w-[180px]">{p.label}</span>
            <span className="text-zinc-500">{p.n}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function JobCreateModal({ users, clients, teams, onClose, onCreated }) {
  const [form, setForm, clearDraft] = useDraft("job", "new", {
    job_number: "", project_name: "", client: "", client_id: "", team: "",
    category: "live",
    given_time_hours: 0, taken_time_hours: 0,
    stage: "assigned", status: "in_progress",
    priority: "normal", rate_override: "",
    stage_assignees: {},
    stage_time_given: { ...STAGE_GIVEN_DEFAULT },
    stage_time_taken: {},
    notes: "", due_date: "",
  });

  // Build the candidate-member list for stage assignees: when a team is set, use that team's members; else fall back to all visible users.
  const assigneeOptions = useMemo(() => {
    if (form.team && Array.isArray(teams)) {
      const t = teams.find((x) => x.team === form.team);
      if (t) return t.members || [];
    }
    return users || [];
  }, [form.team, teams, users]);

  const onClientChange = (id) => {
    const c = clients.find((x) => x.id === id);
    setForm({ ...form, client_id: id, client: c?.name || "", team: c?.team || form.team });
  };

  const setStageGiven = (stage, value) => setForm((f) => ({ ...f, stage_time_given: { ...f.stage_time_given, [stage]: value } }));
  const setStageTaken = (stage, value) => setForm((f) => ({ ...f, stage_time_taken: { ...f.stage_time_taken, [stage]: value === "" ? "" : parseFloat(value) || 0 } }));

  const totalGiven = WORK_STAGES.reduce((sum, s) => sum + (parseFloat(form.stage_time_given[s]) || 0), 0);
  const totalTaken = WORK_STAGES.reduce((sum, s) => sum + (parseFloat(form.stage_time_taken[s]) || 0), 0);
  const efficiency = totalTaken > 0 ? ((totalGiven / totalTaken) * 100).toFixed(2) : "—";

  // Filter clients by team if user typed a team first
  const clientOptions = form.team
    ? clients.filter((c) => !c.team || c.team === form.team)
    : clients;

  const submit = async (e) => {
    e.preventDefault();
    try {
      // Coerce stage_time_taken values to numbers (drop empty strings)
      const stTaken = {};
      for (const s of WORK_STAGES) {
        const v = form.stage_time_taken[s];
        if (v !== "" && v !== null && v !== undefined && !isNaN(parseFloat(v))) stTaken[s] = parseFloat(v);
      }
      const stGiven = {};
      for (const s of WORK_STAGES) {
        const v = form.stage_time_given[s];
        if (v !== "" && v !== null && v !== undefined && !isNaN(parseFloat(v))) stGiven[s] = parseFloat(v);
      }
      const payload = { ...form, stage_time_taken: stTaken, stage_time_given: stGiven };
      if (payload.rate_override === "" || payload.rate_override === null) delete payload.rate_override;
      else payload.rate_override = parseFloat(payload.rate_override);
      // Backend will recompute given_time_hours / taken_time_hours from the stage dicts.
      delete payload.given_time_hours;
      delete payload.taken_time_hours;
      await api.post("/jobs", payload);
      toast.success("Job created");
      clearDraft();
      onCreated();
      onClose();
    } catch { toast.error("Failed to create job"); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="brut-card max-w-2xl w-full p-4 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-2xl font-black">New Job</h2>
            <div className="text-[10px] font-mono text-zinc-500 mt-0.5">DRAFT AUTO-SAVED · Outside-click won&apos;t dismiss</div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => { if (confirm("Discard unsaved draft?")) { clearDraft(); onClose(); } }}
              className="brut-btn-secondary px-2 py-1 text-[10px] font-mono"
              title="Discard the auto-saved draft and close"
            >DISCARD</button>
            <button onClick={onClose} className="brut-btn-secondary p-1.5"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="JOB NUMBER" required>
            <input data-testid="job-number-input" required className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.job_number} onChange={(e) => setForm({ ...form, job_number: e.target.value })} />
          </Field>
          <Field label="PROJECT NAME" required>
            <input data-testid="job-project-input" required className="brut-input w-full px-3 py-2 text-sm" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} />
          </Field>
          <Field label="CLIENT">
            <select className="brut-input w-full px-3 py-2 text-sm" value={form.client_id} onChange={(e) => onClientChange(e.target.value)} data-testid="job-client-select">
              <option value="">— select client —</option>
              {clientOptions.map((c) => <option key={c.id} value={c.id}>{c.name}{c.team ? ` (${c.team})` : ""}</option>)}
            </select>
          </Field>
          <Field label="TEAM">
            <select
              className="brut-input w-full px-3 py-2 text-sm font-mono"
              value={form.team}
              onChange={(e) => setForm({ ...form, team: e.target.value, stage_assignees: {} })}
              data-testid="job-team-select"
            >
              <option value="">— select team —</option>
              {(teams || []).map((t) => (
                <option key={t.team} value={t.team}>{t.team} ({t.member_count})</option>
              ))}
            </select>
          </Field>
          <Field label="CATEGORY">
            <select
              className="brut-input w-full px-3 py-2 text-sm font-mono"
              value={form.category || "live"}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="job-category-select"
            >
              <option value="live">Live (billable)</option>
              <option value="practice">Practice</option>
              <option value="training">Training</option>
            </select>
          </Field>
          <Field label="PRIORITY">
            <select className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="job-priority-select">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </Field>
          <Field label="DUE DATE">
            <input type="date" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </Field>

          {/* Per-stage time tracking */}
          <div className="md:col-span-2 border border-zinc-900 mt-2">
            <div className="bg-zinc-900 text-white px-3 py-1.5 label-mono">PER-STAGE TIME · GIVEN VS TAKEN</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 text-left">
                  <th className="px-2 py-1 label-mono">Stage</th>
                  <th className="px-2 py-1 label-mono">Given (hrs)</th>
                  <th className="px-2 py-1 label-mono">Taken (hrs)</th>
                  <th className="px-2 py-1 label-mono">Assignee</th>
                </tr>
              </thead>
              <tbody>
                {WORK_STAGES.map((s) => (
                  <tr key={s} className="border-t border-zinc-200">
                    <td className="px-2 py-1.5 font-bold text-xs">{STAGE_LABELS[s]}</td>
                    <td className="px-2 py-1.5">
                      <select
                        className="brut-input px-1 py-0.5 text-xs font-mono w-28"
                        value={form.stage_time_given[s] ?? ""}
                        onChange={(e) => setStageGiven(s, parseFloat(e.target.value))}
                        data-testid={`stage-given-${s}`}
                        disabled={STAGE_GIVEN_OPTIONS[s].length === 1}
                      >
                        {STAGE_GIVEN_OPTIONS[s].map((h) => (
                          <option key={h} value={h}>{formatHrLabel(h)}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        type="number"
                        step="0.05"
                        min="0"
                        className="brut-input px-1 py-0.5 text-xs font-mono w-24"
                        value={form.stage_time_taken[s] ?? ""}
                        onChange={(e) => setStageTaken(s, e.target.value)}
                        placeholder="0"
                        data-testid={`stage-taken-${s}`}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <select
                        className="brut-input px-1 py-0.5 text-xs w-full"
                        value={form.stage_assignees[s] || ""}
                        onChange={(e) => setForm({ ...form, stage_assignees: { ...form.stage_assignees, [s]: e.target.value } })}
                        data-testid={`stage-assignee-${s}`}
                      >
                        <option value="">— assign{form.team ? ` from ${form.team}` : ""} —</option>
                        {assigneeOptions.map((u) => <option key={u.user_id} value={u.name}>{u.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-zinc-900 bg-yellow-50 font-bold">
                  <td className="px-2 py-1.5 label-mono">TOTAL</td>
                  <td className="px-2 py-1.5 font-mono text-xs">{totalGiven.toFixed(2)} h</td>
                  <td className="px-2 py-1.5 font-mono text-xs">{totalTaken.toFixed(2)} h</td>
                  <td className="px-2 py-1.5 font-mono text-xs">EFF: {efficiency}{efficiency !== "—" ? "%" : ""}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <Field label="RATE OVERRIDE (LEAVE BLANK FOR CLIENT DEFAULT)" full>
            <input type="number" step="0.01" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.rate_override} onChange={(e) => setForm({ ...form, rate_override: e.target.value })} placeholder="Custom price for this job (optional)" data-testid="rate-override-input" />
          </Field>
          <Field label="NOTES" full>
            <textarea className="brut-input w-full px-3 py-2 text-sm" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <div className="md:col-span-2 flex justify-end gap-2 mt-2">
            <button type="button" onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
            <button type="submit" data-testid="submit-job-btn" className="brut-btn px-4 py-2 text-sm">CREATE JOB</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function JobDetailModal({ job, users, clients, teams, onClose, onChanged, onDuplicate, onAudit }) {
  const [form, setForm] = useState(job);
  useEffect(() => setForm(job), [job]);

  const assigneeOptions = useMemo(() => {
    if (form.team && Array.isArray(teams)) {
      const t = teams.find((x) => x.team === form.team);
      if (t) return t.members || [];
    }
    return users || [];
  }, [form.team, teams, users]);

  const onClientChange = (id) => {
    const c = clients.find((x) => x.id === id);
    setForm({ ...form, client_id: id, client: c?.name || "" });
  };

  const setStageGiven = (stage, value) => setForm((f) => ({ ...f, stage_time_given: { ...(f.stage_time_given || {}), [stage]: value } }));
  const setStageTaken = (stage, value) => setForm((f) => ({ ...f, stage_time_taken: { ...(f.stage_time_taken || {}), [stage]: value === "" ? "" : parseFloat(value) || 0 } }));

  const stGiven = form.stage_time_given || {};
  const stTaken = form.stage_time_taken || {};
  const totalGiven = WORK_STAGES.reduce((sum, s) => sum + (parseFloat(stGiven[s]) || 0), 0);
  const totalTaken = WORK_STAGES.reduce((sum, s) => sum + (parseFloat(stTaken[s]) || 0), 0);
  const efficiency = totalTaken > 0 ? ((totalGiven / totalTaken) * 100).toFixed(2) : "—";

  const save = async () => {
    try {
      // Coerce stage_time_taken / stage_time_given values to numbers (drop empties)
      const stTakenOut = {};
      const stGivenOut = {};
      for (const s of WORK_STAGES) {
        const v = stTaken[s];
        if (v !== "" && v !== null && v !== undefined && !isNaN(parseFloat(v))) stTakenOut[s] = parseFloat(v);
        const g = stGiven[s];
        if (g !== "" && g !== null && g !== undefined && !isNaN(parseFloat(g))) stGivenOut[s] = parseFloat(g);
      }
      const payload = {
        project_name: form.project_name, client: form.client, client_id: form.client_id,
        team: form.team, stage: form.stage, status: form.status, priority: form.priority,
        stage_time_given: stGivenOut,
        stage_time_taken: stTakenOut,
        stage_assignees: form.stage_assignees, notes: form.notes, due_date: form.due_date,
      };
      if (form.rate_override === "" || form.rate_override === null || form.rate_override === undefined) {
        payload.rate_override = null;
      } else {
        payload.rate_override = parseFloat(form.rate_override);
      }
      await api.patch(`/jobs/${job.id}`, payload);
      toast.success("Saved");
      onChanged(); onClose();
    } catch { toast.error("Save failed"); }
  };

  const del = async () => {
    if (!confirm("Delete this job?")) return;
    try { await api.delete(`/jobs/${job.id}`); toast.success("Deleted"); onChanged(); onClose(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="brut-card max-w-3xl w-full p-4 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-mono">{form.job_number}</div>
            <h2 className="font-heading text-2xl font-black">{form.project_name}</h2>
            <div className="text-[10px] font-mono text-zinc-500 mt-0.5">Outside-click won&apos;t dismiss · use X / CANCEL</div>
          </div>
          <button onClick={onClose} className="brut-btn-secondary p-1.5"><X className="w-4 h-4" /></button>
        </div>

        {/* Surveydrafts.us mirror — read-only complete job detail (only for SD-sourced jobs) */}
        {job.source === "surveydrafts" && job.surveydrafts_id && (
          <div className="mb-4">
            <SurveydraftsJobPanel jobId={job.id} />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="STAGE">
            <select className="brut-input w-full px-3 py-2 text-sm" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}>
              {["assigned","drafter","qa1","qa2","final_qc","sent_to_client"].map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
            </select>
          </Field>
          <Field label="STATUS">
            <select className="brut-input w-full px-3 py-2 text-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="sent_to_client">Sent to Client</option>
              <option value="hold">Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </Field>
          <Field label="CLIENT">
            <select className="brut-input w-full px-3 py-2 text-sm" value={form.client_id || ""} onChange={(e) => onClientChange(e.target.value)}>
              <option value="">— select client —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="TEAM">
            <select
              className="brut-input w-full px-3 py-2 text-sm font-mono"
              value={form.team || ""}
              onChange={(e) => setForm({ ...form, team: e.target.value })}
              data-testid="detail-job-team-select"
            >
              <option value="">— select team —</option>
              {(teams || []).map((t) => (
                <option key={t.team} value={t.team}>{t.team} ({t.member_count})</option>
              ))}
              {/* If the job's team isn't in the visible list, still show it so we don't lose context */}
              {form.team && !(teams || []).some((t) => t.team === form.team) && (
                <option value={form.team}>{form.team}</option>
              )}
            </select>
          </Field>
          <Field label="CATEGORY">
            <select
              className="brut-input w-full px-3 py-2 text-sm font-mono"
              value={form.category || "live"}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="detail-job-category-select"
            >
              <option value="live">Live (billable)</option>
              <option value="practice">Practice</option>
              <option value="training">Training</option>
            </select>
          </Field>
          <Field label="PRIORITY">
            <select className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.priority || "normal"} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </Field>
          <Field label="DUE DATE">
            <input type="date" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </Field>

          {/* Per-stage time tracking */}
          <div className="md:col-span-2 border border-zinc-900 mt-2">
            <div className="bg-zinc-900 text-white px-3 py-1.5 label-mono">PER-STAGE TIME · GIVEN VS TAKEN</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 text-left">
                  <th className="px-2 py-1 label-mono">Stage</th>
                  <th className="px-2 py-1 label-mono">Given (hrs)</th>
                  <th className="px-2 py-1 label-mono">Taken (hrs)</th>
                  <th className="px-2 py-1 label-mono">Assignee</th>
                </tr>
              </thead>
              <tbody>
                {WORK_STAGES.map((s) => (
                  <tr key={s} className="border-t border-zinc-200">
                    <td className="px-2 py-1.5 font-bold text-xs">{STAGE_LABELS[s]}</td>
                    <td className="px-2 py-1.5">
                      <select
                        className="brut-input px-1 py-0.5 text-xs font-mono w-28"
                        value={stGiven[s] ?? STAGE_GIVEN_DEFAULT[s]}
                        onChange={(e) => setStageGiven(s, parseFloat(e.target.value))}
                        data-testid={`detail-stage-given-${s}`}
                        disabled={STAGE_GIVEN_OPTIONS[s].length === 1}
                      >
                        {STAGE_GIVEN_OPTIONS[s].map((h) => (
                          <option key={h} value={h}>{formatHrLabel(h)}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        type="number"
                        step="0.05"
                        min="0"
                        className="brut-input px-1 py-0.5 text-xs font-mono w-24"
                        value={stTaken[s] ?? ""}
                        onChange={(e) => setStageTaken(s, e.target.value)}
                        placeholder="0"
                        data-testid={`detail-stage-taken-${s}`}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <select
                        className="brut-input px-1 py-0.5 text-xs w-full"
                        value={form.stage_assignees?.[s] || ""}
                        onChange={(e) => setForm({ ...form, stage_assignees: { ...(form.stage_assignees || {}), [s]: e.target.value } })}
                        data-testid={`detail-stage-assignee-${s}`}
                      >
                        <option value="">— assign{form.team ? ` from ${form.team}` : ""} —</option>
                        {assigneeOptions.map((u) => <option key={u.user_id} value={u.name}>{u.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-zinc-900 bg-yellow-50 font-bold">
                  <td className="px-2 py-1.5 label-mono">TOTAL</td>
                  <td className="px-2 py-1.5 font-mono text-xs">{totalGiven.toFixed(2)} h</td>
                  <td className="px-2 py-1.5 font-mono text-xs">{totalTaken.toFixed(2)} h</td>
                  <td className="px-2 py-1.5 font-mono text-xs">EFF: {efficiency}{efficiency !== "—" ? "%" : ""}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <Field label="RATE OVERRIDE (BLANK = USE CLIENT DEFAULT)" full>
            <input type="number" step="0.01" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.rate_override ?? ""} onChange={(e) => setForm({ ...form, rate_override: e.target.value })} placeholder="Custom price for this specific job" />
          </Field>
          <Field label="NOTES" full>
            <textarea className="brut-input w-full px-3 py-2 text-sm" rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </div>

        <div className="mt-4 p-3 bg-yellow-100 border border-zinc-900 font-mono text-xs">
          EFFICIENCY: <b>{efficiency}{efficiency !== "—" ? "%" : ""}</b> · TOTAL TAKEN: <b>{totalTaken.toFixed(2)} h</b> / GIVEN: <b>{totalGiven.toFixed(2)} h</b>
        </div>

        <div className="flex justify-between mt-4 flex-wrap gap-2">
          <button onClick={del} className="text-xs font-bold text-red-600 hover:underline" data-testid="delete-job-btn">DELETE JOB</button>
          <div className="flex gap-2">
            {onAudit && (
              <button onClick={() => onAudit(job)} className="brut-btn-secondary px-3 py-2 text-sm flex items-center gap-1" data-testid="job-audit-btn">
                <History className="w-3.5 h-3.5" /> HISTORY
              </button>
            )}
            {onDuplicate && (
              <button onClick={onDuplicate} className="brut-btn-secondary px-3 py-2 text-sm flex items-center gap-1" data-testid="duplicate-job-btn">
                <Copy className="w-3.5 h-3.5" /> DUPLICATE
              </button>
            )}
            <button onClick={save} className="brut-btn px-4 py-2 text-sm" data-testid="save-job-btn">SAVE CHANGES</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full, required }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="label-mono mb-1">{label}{required && <span className="text-red-600"> *</span>}</div>
      {children}
    </label>
  );
}
